import React, { useState, useEffect } from 'react';
import styles from './FilterView.module.css';
import {
  DEPARTMENT_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  PROPERTY_STATE_OPTIONS,
  CURRENCY_OPTIONS,
  ORDER_BY_OPTIONS,
  COUNT_FIELDS,
  COUNT_VALUE_OPTIONS,
  MEASURE_FIELDS,
  BOOLEAN_GROUP_CONFIG,
} from '../../utils/filterOptions';
import { DEFAULT_FILTERS, hasActiveFilters } from '../../utils/propertyCatalogFilters';

// FilterView unificado: filtros básicos siempre visibles + panel avanzado colapsable.
// Reutilizado por Venta, Alquiler, Luxury y Desarrollos.
// Props:
//  - onFilterChange(filters): callback al aplicar/limpiar filtros.
//  - initialFilters: estado inicial (leído de la URL).
//  - showCurrency: oculta el selector de moneda (ej. Luxury siempre USD).
const FilterView = ({ onFilterChange, initialFilters, showCurrency = true }) => {
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, ...initialFilters });
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    setFilters({ ...DEFAULT_FILTERS, ...initialFilters });
  }, [initialFilters]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Alterna una opción dentro de un grupo booleano (amenities, characteristics, ...).
  const handleToggleGroup = (groupKey, optionValue) => {
    setFilters((prev) => {
      const current = Array.isArray(prev[groupKey]) ? prev[groupKey] : [];
      const next = current.includes(optionValue)
        ? current.filter((v) => v !== optionValue)
        : [...current, optionValue];
      return { ...prev, [groupKey]: next };
    });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    onFilterChange(filters);
  };

  const handleClear = () => {
    const cleared = { ...DEFAULT_FILTERS };
    setFilters(cleared);
    onFilterChange(cleared);
  };

  return (
    <form className={styles.filterContainer} onSubmit={handleFilterSubmit}>
      {/* ===== Filtros básicos ===== */}
      <div className={styles.basicRow}>
        <select
          name="departments"
          className={styles.dropdown}
          onChange={handleChange}
          value={filters.departments}
        >
          <option value="">Departamento</option>
          {DEPARTMENT_OPTIONS.map((dep) => (
            <option key={dep} value={dep}>{dep}</option>
          ))}
        </select>

        <select
          name="propertyType"
          className={styles.dropdown}
          onChange={handleChange}
          value={filters.propertyType}
        >
          <option value="">Tipo de propiedad</option>
          {PROPERTY_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        {showCurrency && (
          <select
            name="currency"
            className={styles.dropdown}
            onChange={handleChange}
            value={filters.currency}
          >
            <option value="">Moneda</option>
            {CURRENCY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )}

        <input
          type="text"
          name="minPrice"
          placeholder="Precio mínimo"
          className={styles.inputField}
          value={filters.minPrice}
          onChange={handleChange}
        />
        <input
          type="text"
          name="maxPrice"
          placeholder="Precio máximo"
          className={styles.inputField}
          value={filters.maxPrice}
          onChange={handleChange}
        />

        <select
          name="orderBy"
          className={styles.dropdown}
          onChange={handleChange}
          value={filters.orderBy}
        >
          {ORDER_BY_OPTIONS.map((opt) => (
            <option key={opt.value || 'relevancia'} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <button type="submit" className={styles.filterButton}>Filtrar</button>
      </div>

      {/* ===== Acciones secundarias ===== */}
      <div className={styles.actionsRow}>
        <button
          type="button"
          className={styles.toggleButton}
          onClick={() => setShowAdvanced((prev) => !prev)}
        >
          {showAdvanced ? 'Ocultar filtros avanzados ▲' : 'Filtros avanzados ▼'}
        </button>
        {hasActiveFilters(filters) && (
          <button type="button" className={styles.clearButton} onClick={handleClear}>
            Limpiar filtros
          </button>
        )}
      </div>

      {/* ===== Panel avanzado ===== */}
      {showAdvanced && (
        <div className={styles.advancedPanel}>
          {/* Estado y ambientes (conteos) */}
          <div className={styles.advancedSection}>
            <h4 className={styles.sectionTitle}>Estado y ambientes</h4>
            <div className={styles.gridFields}>
              <label className={styles.fieldLabel}>
                Estado
                <select
                  name="propertyState"
                  className={styles.dropdown}
                  onChange={handleChange}
                  value={filters.propertyState}
                >
                  <option value="">Cualquiera</option>
                  {PROPERTY_STATE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>

              {COUNT_FIELDS.map((field) => (
                <label key={field.name} className={styles.fieldLabel}>
                  {field.label}
                  <select
                    name={field.name}
                    className={styles.dropdown}
                    onChange={handleChange}
                    value={filters[field.name]}
                  >
                    <option value="">Cualquiera</option>
                    {COUNT_VALUE_OPTIONS.map((v) => (
                      <option key={v} value={v}>{v === '6' ? '6+' : v}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </div>

          {/* Medidas (rangos) y antigüedad */}
          <div className={styles.advancedSection}>
            <h4 className={styles.sectionTitle}>Medidas y antigüedad</h4>
            <div className={styles.gridFields}>
              {MEASURE_FIELDS.map((field) => (
                <div key={field.min} className={styles.rangeField}>
                  <span className={styles.fieldLabel}>{field.label}</span>
                  <div className={styles.rangeInputs}>
                    <input
                      type="number"
                      min="0"
                      name={field.min}
                      placeholder="Mín"
                      className={styles.inputField}
                      value={filters[field.min]}
                      onChange={handleChange}
                    />
                    <input
                      type="number"
                      min="0"
                      name={field.max}
                      placeholder="Máx"
                      className={styles.inputField}
                      value={filters[field.max]}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              ))}

              <div className={styles.rangeField}>
                <span className={styles.fieldLabel}>Antigüedad (años)</span>
                <div className={styles.rangeInputs}>
                  <input
                    type="number"
                    min="0"
                    name="minAge"
                    placeholder="Mín"
                    className={styles.inputField}
                    value={filters.minAge}
                    onChange={handleChange}
                  />
                  <input
                    type="number"
                    min="0"
                    name="maxAge"
                    placeholder="Máx"
                    className={styles.inputField}
                    value={filters.maxAge}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Grupos booleanos (checkboxes) */}
          {BOOLEAN_GROUP_CONFIG.map((group) => (
            <div key={group.key} className={styles.advancedSection}>
              <h4 className={styles.sectionTitle}>{group.title}</h4>
              <div className={styles.checkboxGrid}>
                {group.options.map((opt) => (
                  <label key={opt.value} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={(filters[group.key] || []).includes(opt.value)}
                      onChange={() => handleToggleGroup(group.key, opt.value)}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
          ))}

          <div className={styles.advancedActions}>
            <button type="submit" className={styles.filterButton}>Aplicar filtros</button>
          </div>
        </div>
      )}
    </form>
  );
};

export default FilterView;
